import { NATIONAL_DISTRICT } from './index.js';
/**
 * RBAC-org-scope for en person plassert på en ORBAT-enhet. Slektslinjen
 * oversettes etter HV-strukturen: `distrikt`-enheten er `district`, HV-området
 * (echelon `kompani`) er `area`, `tropp` er `troop`, `lag` er `team` —
 * enhets-ID-ene ER org-ID-ene (samme konvensjon som `User.org`, jf. seeden der
 * distriktene heter «HV-02» osv.). En enhet UTENFOR distriktsstrukturen
 * (forsvarsgren, FOH, brigade/bataljon rett under en gren) får nasjonal scope.
 */
export function orgFraOrbatLinje(linje) {
    let district;
    let area;
    let troop;
    let team;
    for (const l of linje) {
        if (l.echelon === 'distrikt' && !district)
            district = l.id;
        else if (l.echelon === 'kompani' && !area)
            area = l.id;
        else if (l.echelon === 'tropp' && !troop)
            troop = l.id;
        else if (l.echelon === 'lag' && !team)
            team = l.id;
    }
    if (!district)
        return { district: NATIONAL_DISTRICT };
    return { district, area, troop, team };
}
/** Foreslått SKADE-rolle for en person på en enhet: førerrollen på nivået.
 * (Operatøren kan alltid velge lavere — f.eks. `menig` på et lag.) */
export function foreslattRolleForEchelon(ech) {
    switch (ech) {
        case 'enkeltmann':
        case 'makkerpar':
            return 'menig';
        case 'lag':
            return 'lagfører';
        case 'tropp':
            return 'troppssjef';
        case 'kompani':
            return 'områdesjef';
        case 'bataljon':
        case 'brigade':
        case 'distrikt':
            return 'distriktsstab';
        case 'forsvarsgren':
        case 'foh':
            return 'foh';
    }
}
/** Maks feilforsøk på en utfordring før den forkastes. */
export const MAKS_UTFORDRINGSFORSOK = 3;
/** Bygger en ny token (koden lages av kalleren, f.eks. crypto.randomUUID). */
export function issueToken(params) {
    const now = params.now ?? Date.now();
    return {
        code: params.code,
        role: params.role,
        org: params.org,
        subject: params.subject,
        expiresAt: now + params.ttlMs,
        maxUses: params.maxUses ?? 1,
        uses: 0,
        issuedBy: params.issuedBy,
    };
}
/** Sjekker gyldighet uten å endre tokenet. */
export function checkToken(token, now = Date.now()) {
    if (!token)
        return { ok: false, reason: 'ukjent' };
    if (now > token.expiresAt)
        return { ok: false, reason: 'utløpt' };
    if (token.maxUses > 0 && token.uses >= token.maxUses)
        return { ok: false, reason: 'oppbrukt' };
    return { ok: true, role: token.role, org: token.org };
}
/** Løser inn en token: validerer og øker bruksteller. Returnerer resultatet. */
export function redeemToken(token, now = Date.now()) {
    const res = checkToken(token, now);
    if (res.ok && token)
        token.uses += 1;
    return res;
}
/**
 * Butikk for enrolment-tokens.
 *
 * PERSISTENS ER IKKE VALGFRITT I DRIFT — og det var det, med reell konsekvens.
 * Butikken var en ren `Map` i minnet. Følgen: HVER omstart av hubben slettet
 * alle utestående enrolment-koder. Og hubben startes på nytt ved hver deploy og
 * hver auto-oppdatering, altså flere ganger i timen under aktiv utvikling.
 *
 * Symptomet operatøren møtte var «enrolment avvist: ukjent» på en kode som var
 * riktig avskrevet og ikke utløpt. `ukjent` betyr at hubben ikke har koden i det
 * hele tatt — den var glemt, ikke gal. Verre: feilmeldingen peker mot at brukeren
 * har skrevet feil, mens feilen ligger i tjenesten.
 *
 * Uten `persistens` oppfører butikken seg som før (in-memory) — det er riktig for
 * tester og utvikling, men hubben SKAL sette den i drift.
 */
export class EnrolmentStore {
    persistens;
    tokens = new Map();
    /**
     * Utestående utfordringer. BEVISST bare i minnet, i motsetning til tokens:
     * en utfordring lever i minutter mens brukeren står med telefonen, og går den
     * tapt i en omstart er kuren å be om en ny engangskode — noe brukeren gjør
     * selv på sekunder. Et token deles ut dager i forveien, og der var
     * minne-lagringen en reell driftsfeil (se klassen over).
     */
    challenges = new Map();
    constructor(persistens, now = Date.now()) {
        this.persistens = persistens;
        if (!persistens)
            return;
        try {
            for (const t of persistens.les()) {
                // Utløpte kastes ved lasting. Ellers vokser fila i det uendelige med
                // koder som aldri kan brukes igjen, og en gammel kode ville ligget
                // igjen på disk lenge etter at den sluttet å virke.
                if (t && typeof t.code === 'string' && now <= t.expiresAt)
                    this.tokens.set(t.code, t);
            }
        }
        catch { /* fil finnes ikke ennå / korrupt → start tomt, som før */ }
    }
    put(token) {
        this.tokens.set(token.code, token);
        this.lagre();
    }
    /** Sjekker gyldighet UTEN å øke bruksteller (til rekkefølge-sikring før vi
     * avslører om et bruker-id finnes — jf. enumereringsvern). */
    check(code, now = Date.now()) {
        return checkToken(this.tokens.get(code), now);
    }
    redeem(code, now = Date.now()) {
        const res = redeemToken(this.tokens.get(code), now);
        // Lagre BARE når brukstelleren faktisk økte. Et avvist forsøk skal ikke
        // skrive til disk — ellers kan gjentatt gjetting brukes til å presse I/O.
        if (res.ok)
            this.lagre();
        return res;
    }
    /**
     * TRINN 1 av en bundet innløsning: sjekk at koden er gyldig OG utstedt til
     * den som påstår å være mottakeren, og registrer en utfordring. Kalleren
     * (hubben) lager `nonce` og sender den som GNÅ-melding til adressaten.
     *
     * Merk at avslaget «feil-mottaker» kommer FØR noen engangskode sendes — ellers
     * kunne noen brukt en gyldig kode til å spamme en annens telefon.
     */
    begin(params) {
        const now = params.now ?? Date.now();
        const token = this.tokens.get(params.code);
        const res = checkToken(token, now);
        if (!res.ok)
            return { ok: false, reason: res.reason };
        if (!token || token.subject !== params.claimedSubject) {
            return { ok: false, reason: 'feil-mottaker' };
        }
        this.challenges.set(params.code, {
            code: params.code,
            subject: token.subject,
            nonce: params.nonce,
            expiresAt: now + params.ttlMs,
            attempts: 0,
        });
        return { ok: true, subject: token.subject };
    }
    /**
     * TRINN 2: engangskoden tilbake fra adressaten. Først her økes tokenets
     * bruksteller — en påbegynt, aldri fullført innløsning skal ikke brenne
     * nøkkelen (ellers kunne en fremmed med en videresendt kode ødelegge den for
     * den rettmessige mottakeren ved å starte trinn 1).
     */
    complete(code, nonce, now = Date.now()) {
        const utfordring = this.challenges.get(code);
        if (!utfordring || now > utfordring.expiresAt) {
            this.challenges.delete(code);
            return { ok: false, reason: 'ingen-utfordring' };
        }
        if (utfordring.nonce !== nonce) {
            utfordring.attempts += 1;
            if (utfordring.attempts >= MAKS_UTFORDRINGSFORSOK) {
                this.challenges.delete(code);
                return { ok: false, reason: 'for-mange-forsøk' };
            }
            return { ok: false, reason: 'feil-engangskode' };
        }
        const res = this.redeem(code, now);
        this.challenges.delete(code); // engangs, uansett utfall
        if (!res.ok)
            return { ok: false, reason: res.reason };
        return { ok: true, role: res.role, org: res.org, subject: utfordring.subject };
    }
    /** Antall utestående utfordringer (til helsesjekk/test). */
    utfordringer() {
        return this.challenges.size;
    }
    revoke(code) {
        this.challenges.delete(code);
        if (this.tokens.delete(code))
            this.lagre();
    }
    size() {
        return this.tokens.size;
    }
    lagre() {
        // En feilet skriving skal ALDRI velte innløsningen som pågår: brukeren har
        // fått kontoen sin, og å kaste her ville rullet tilbake en handling som
        // allerede er utført i brukerlageret.
        try {
            this.persistens?.skriv([...this.tokens.values()]);
        }
        catch { /* disk full/RO */ }
    }
}
//# sourceMappingURL=enrolment.js.map